/* ============================================================
   SNOW — analog static / "snow" generator on a canvas
   ------------------------------------------------------------
   Renders chunky grayscale noise at a low internal resolution
   and lets CSS stretch it (image-rendering: pixelated) so it
   reads like coarse analog snow without melting the CPU.
   Throttled to ~28fps. start()/stop() control the RAF loop.

   Ported verbatim from the prototype's snow.js — behavior-identical.
   ============================================================ */
export class Snow {
  private ctx: CanvasRenderingContext2D
  private w = 160
  private h = 120
  private image: ImageData
  private running = false
  private last = 0
  private interval = 1000 / 28
  private raf: number | null = null
  intensity = 1 // 0..1 — scales contrast
  private scanRoll = 0 // a faint rolling bright band

  constructor(canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D
    canvas.width = this.w
    canvas.height = this.h
    this.image = this.ctx.createImageData(this.w, this.h)
  }

  private draw() {
    const data = this.image.data
    const n = this.w * this.h
    const amp = 90 + this.intensity * 165
    const base = 18
    // rolling horizontal band position
    this.scanRoll = (this.scanRoll + 2) % this.h
    for (let i = 0; i < n; i++) {
      let v = base + Math.random() * amp
      // occasional bright speckle
      if (Math.random() < 0.015) v = 230 + Math.random() * 25
      const row = (i / this.w) | 0
      const dist = Math.abs(row - this.scanRoll)
      if (dist < 6) v = Math.min(255, v + (6 - dist) * 9)
      const p = i * 4
      data[p] = data[p + 1] = data[p + 2] = v
      data[p + 3] = 255
    }
    this.ctx.putImageData(this.image, 0, 0)
  }

  private tick = (t: number) => {
    if (!this.running) return
    this.raf = requestAnimationFrame(this.tick)
    if (t - this.last < this.interval) return
    this.last = t
    this.draw()
  }

  start() {
    this.ctx.canvas.style.opacity = '' // clear any fade-out left by hide() → CSS owns visibility again
    if (this.running) return
    this.running = true
    this.last = 0
    this.draw() // paint one frame immediately (RAF may be paused while hidden)
    this.raf = requestAnimationFrame(this.tick)
  }

  stop() {
    this.running = false
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = null
  }

  /** Stop AND fade the canvas out (it can't go transparent — alpha:false — so we fade the element).
   *  Used on power-off so the static doesn't sit at full brightness through the CRT collapse and then
   *  snap to black; it leaves before the turn-off animation. start() clears the fade. */
  hide() {
    this.stop()
    this.ctx.canvas.style.opacity = '0'
  }

  // A single full-frame burst (used during channel changes even
  // when snow isn't the resting state).
  burst() {
    this.draw()
  }
}
